import { useWorkflowMutation, useWorkflowCompletion } from "./react.tsx";
import { workflowStartBusyKey } from "./mutation-model.ts";
import { parseWorkflowJson } from "./form.ts";
import { Button, Dialog } from "@cloudflare/kumo";
import { useCallback, useEffect, useState } from "react";
import type { WorkflowInstanceRecord } from "../../api/workflows/model.ts";

// Adapted from workers-sdk's local-explorer-ui CreateInstanceDialog, wired
// to this project's startWorkflow client.
interface CreateWorkflowInstanceDialogProps {
  workspaceId: string;
  className: string;
  expectedWorkerId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (instance: WorkflowInstanceRecord) => void;
}

export function CreateWorkflowInstanceDialog({
  workspaceId,
  className,
  expectedWorkerId,
  open,
  onOpenChange,
  onCreated
}: CreateWorkflowInstanceDialogProps) {
  const selectionKey = JSON.stringify(["create", workspaceId, className, expectedWorkerId ?? ""]);
  const mutation = useWorkflowMutation(selectionKey, open, workflowStartBusyKey(workspaceId, className));
  const creating = mutation.busy, error = mutation.snapshot.error;
  const [instanceId, setInstanceId] = useState("");
  const [params, setParams] = useState("");
  const [paramsError, setParamsError] = useState<string | null>(null);
  const resetForm = useCallback(() => { setInstanceId("");setParams("");setParamsError(null); }, []);
  useEffect(() => { resetForm(); }, [selectionKey, open, resetForm]);
  useWorkflowCompletion(mutation, snapshot => { if (snapshot.result) { resetForm();onCreated(snapshot.result); } });
  function handleOpenChange(next: boolean) { if (!next) { mutation.dispatch({ _tag: "Select", selectionKey, active: false });resetForm(); }onOpenChange(next); }
  function handleCreate(): void {
    const parsed = parseWorkflowJson(params);if (!parsed.valid) { setParamsError("Invalid JSON");return; }setParamsError(null);
    mutation.dispatch({ _tag: "Start", selectionKey, target: { workspaceId, className }, options: { ...(instanceId.trim() ? { id: instanceId.trim() } : {}), ...(parsed.value === undefined ? {} : { params: parsed.value }), ...(expectedWorkerId === undefined ? {} : { expectedWorkerId }) } });
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog size="lg">
        {/* Header */}
        <div className="border-b border-kumo-fill px-6 pt-6 pb-4">
          <Dialog.Title className="text-lg font-semibold text-kumo-default">
            Trigger this workflow?
          </Dialog.Title>
          <p className="mt-1 text-sm text-kumo-subtle">
            Manually trigger an instance of {className} using the payload
            below.
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {error && (
            <div className="mb-5 rounded-lg border border-kumo-danger/20 bg-kumo-danger/8 p-3 text-sm text-kumo-danger">
              {error}
            </div>
          )}

          <div className="mb-5">
            <label
              className="mb-2 block text-sm font-medium text-kumo-default"
              htmlFor="workflow-instance-id"
            >
              Instance ID{" "}
              <span className="font-normal text-kumo-subtle">(optional)</span>
            </label>
            <input
              id="workflow-instance-id"
              className="focus-visible:ring-kumo-ring w-full rounded-lg border border-kumo-fill bg-kumo-base px-3 py-2.5 text-sm text-kumo-default placeholder:text-kumo-subtle focus:border-kumo-brand focus:outline-none focus-visible:ring-2"
              onChange={(e) => setInstanceId(e.target.value)}
              placeholder="Auto-generated UUID if empty"
              type="text"
              value={instanceId}
            />
          </div>

          <div>
            <label
              className="mb-2 block text-sm font-medium text-kumo-default"
              htmlFor="workflow-instance-params"
            >
              Params
            </label>
            <textarea
              id="workflow-instance-params"
              className={`focus-visible:ring-kumo-ring w-full resize-y rounded-lg border bg-kumo-base px-3 py-2.5 font-mono text-sm text-kumo-default placeholder:text-kumo-subtle focus:outline-none focus-visible:ring-2 ${
                paramsError
                  ? "border-kumo-danger focus:border-kumo-danger"
                  : "border-kumo-fill focus:border-kumo-brand"
              }`}
              onChange={(e) => {
                setParams(e.target.value);
                if (paramsError) {
                  setParamsError(null);
                }
              }}
              placeholder='{"key": "value"}'
              rows={8}
              value={params}
            />
            {paramsError ? (
              <p className="mt-1 text-xs text-kumo-danger">{paramsError}</p>
            ) : (
              <p className="mt-1 text-xs text-kumo-subtle">
                JSON payload passed to the workflow
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-kumo-fill px-6 py-4">
          <Button
            variant="secondary"
            onClick={() => handleOpenChange(false)}
            disabled={creating || !mutation.ready}
            aria-busy={!mutation.ready}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={creating || !mutation.ready}
            aria-busy={!mutation.ready}
            loading={creating}
            onClick={() => void handleCreate()}
          >
            {creating ? "Triggering..." : "Trigger Instance"}
          </Button>
        </div>
      </Dialog>
    </Dialog.Root>
  );
}
